import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Plus, Edit, Trash2, Eye, EyeOff } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';

import { API_URL } from '../../config/api';
const API = `${API_URL}/api`;

export default function AdminBlog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchPosts = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/admin/blog`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPosts(response.data);
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPosts();
  }, []);

  const handleDelete = async (postId) => {
    if (!window.confirm('Es-tu sûr de vouloir supprimer cet article ?')) return;

    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API}/admin/blog/${postId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Article supprimé');
      fetchPosts();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleTogglePublish = async (post) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(`${API}/admin/blog/${post.id}`, 
        { published: !post.published },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(post.published ? 'Article dépublié' : 'Article publié');
      fetchPosts();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div data-testid="admin-blog">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-rm-text">Articles</h1>
          <p className="text-rm-text-secondary">Gère tes articles de blog</p>
        </div>
        <Link to="/admin/blog/new">
          <Button className="bg-rm-primary hover:bg-rm-primary/90 text-white rounded-xl" data-testid="new-post-btn">
            <Plus className="w-5 h-5 mr-2" />
            Nouvel article
          </Button>
        </Link>
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="spinner"></div>
        </div>
      ) : posts.length === 0 ? (
        <Card>
          <CardContent className="py-20 text-center">
            <p className="text-rm-text-secondary mb-4">Aucun article pour le moment</p>
            <Link to="/admin/blog/new">
              <Button className="bg-rm-primary hover:bg-rm-primary/90 text-white rounded-xl">
                Crée ton premier article
              </Button>
            </Link>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {posts.map((post, index) => (
            <motion.div
              key={post.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <Card className="hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    {post.image_url && (
                      <img 
                        src={post.image_url}
                        alt={post.title}
                        className="w-24 h-24 object-cover rounded-xl flex-shrink-0"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <Badge variant={post.published ? 'default' : 'secondary'}>
                              {post.published ? 'Publié' : 'Brouillon'}
                            </Badge>
                            <span className="text-xs text-rm-text-secondary">{post.category}</span>
                          </div>
                          <h3 className="font-semibold text-rm-text text-lg mb-1 truncate">
                            {post.title}
                          </h3>
                          <p className="text-sm text-rm-text-secondary line-clamp-2 mb-2">
                            {post.excerpt}
                          </p>
                          <p className="text-xs text-rm-text-secondary">
                            {post.author} • {formatDate(post.created_at)}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <button
                            onClick={() => handleTogglePublish(post)}
                            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                            title={post.published ? 'Dépublier' : 'Publier'}
                          >
                            {post.published ? (
                              <EyeOff className="w-5 h-5 text-slate-500" />
                            ) : (
                              <Eye className="w-5 h-5 text-green-500" />
                            )}
                          </button>
                          <Link 
                            to={`/admin/blog/edit/${post.id}`}
                            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                          >
                            <Edit className="w-5 h-5 text-blue-500" />
                          </Link>
                          <button
                            onClick={() => handleDelete(post.id)}
                            className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                          >
                            <Trash2 className="w-5 h-5 text-red-500" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
